import React from 'react';
import { withTranslation } from 'react-i18next';
import { RAG_INDEXABLE_EXTENSIONS, hasRagIndexableExtension, isRagSidecar, ragSidecarName } from 'data-types';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Upload, Trash2, FileText, Image, File, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { uploadFile, listDocuments, deleteDocument, type IDocumentItem } from '../services/rest';

interface IDocumentManagerProps {
  t: (key: string, options?: any) => string;
  JWT: string | null;
}

interface IDocumentManagerState {
  documents: IDocumentItem[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  showOnlySourceDocuments: boolean;
}

class DocumentManagerBase extends React.Component<IDocumentManagerProps, IDocumentManagerState> {
  private fileInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: IDocumentManagerProps) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      documents: [],
      isLoading: false,
      isUploading: false,
      error: null,
      showOnlySourceDocuments: true,
    };
  }

  componentDidMount() {
    this.loadDocuments();
  }

  /**
   * Loads the list of documents from the backend
   */
  loadDocuments = async () => {
    const { JWT, t } = this.props;
    if (!JWT) return;

    this.setState({ isLoading: true, error: null });

    try {
      const documents = await listDocuments(JWT);
      this.setState({ documents });
    } catch (error: any) {
      console.error('Error loading documents:', error);
      const errorMessage = error.message || t('messages.error');
      this.setState({ error: errorMessage });
      toast.error(t('documents.loadError', { error: errorMessage }));
    } finally {
      this.setState({ isLoading: false });
    }
  };

  /**
   * Handles file upload button click
   */
  handleUploadClick = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  /**
   * Handles file selection and upload
   */
  handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const { t, JWT } = this.props;

    if (!JWT) {
      toast.error(t('auth.required'));
      return;
    }

    this.setState({ isUploading: true });

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        if (!hasRagIndexableExtension(file.name)) {
          toast.error(t('documents.unsupportedFileType', { fileName: file.name, fileType: file.type || 'unknown' }));
          return null;
        }

        // Validate file size (API Gateway hard limit is 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        if (file.size > maxSize) {
          toast.error(t('documents.fileTooLarge', { fileName: file.name, fileSizeMB }));
          return null;
        }

        try {
          await uploadFile({
            JWT,
            file,
            fileName: file.name,
            path: 'rag',
          });

          toast.success(t('documents.uploadSuccess', { fileName: file.name }), { autoClose: 2000 });
          return true;
        } catch (error: any) {
          console.error('Error uploading file:', error);

          if (error.message?.includes('status: 413')) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
            toast.error(t('documents.fileTooLarge', { fileName: file.name, fileSizeMB }));
          } else if (error.message?.includes('Max retries exceeded')) {
            toast.error(t('documents.uploadRetryFailed', { fileName: file.name }));
          } else {
            toast.error(t('documents.uploadFailed', { fileName: file.name, error: error.message }));
          }

          return null;
        }
      });

      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(Boolean).length;

      if (successCount > 0) {
        this.loadDocuments();
        setTimeout(
          async () => {
            // Reload documents list again with timeout based on number of uploaded files plus 10000 (for backend\lambdas\bucket-processor\bucketProcessor.js to do its processing .. TODO: improve)
            await this.loadDocuments();
          },
          10000 + 1000 * successCount,
        );
      }
    } catch (error) {
      console.error('Error in file upload process:', error);
      toast.error(t('messages.error'));
    } finally {
      this.setState({ isUploading: false });
    }

    // Clear the input
    if (event.target) {
      event.target.value = '';
    }
  };

  /**
   * Handles document deletion
   */
  handleDeleteDocument = async (documentId: string, fileName: string) => {
    const { JWT, t } = this.props;
    if (!JWT) return;

    if (!window.confirm(t('documents.deleteConfirm', { fileName }))) {
      return;
    }

    try {
      await deleteDocument(JWT, documentId);
      toast.success(t('documents.deleteSuccess', { fileName }));

      // The bucket processor also deletes the document's sidecar; drop both rows without
      // waiting for a server reload.
      this.setState((prevState) => ({
        documents: prevState.documents.filter((doc) => doc.document_id !== documentId && doc.file_name !== ragSidecarName(fileName)),
      }));
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error(t('documents.deleteError', { fileName, error: error.message }));
    }
  };

  /**
   * Gets the appropriate icon for a file type
   */
  getFileIcon = (fileType: string, fileName: string) => {
    if (fileType.startsWith('image/')) {
      return <Image size={20} />;
    }
    if (fileType === 'application/pdf') {
      return <FileText size={20} />;
    }
    return <File size={20} />;
  };

  /**
   * Formats file size for display
   */
  formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  /**
   * Formats date for display
   */
  formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  render() {
    const { t } = this.props;
    const { documents, isLoading, isUploading, error, showOnlySourceDocuments } = this.state;
    const visibleDocuments = showOnlySourceDocuments ? documents.filter((doc) => !isRagSidecar(doc.file_name)) : documents;

    return (
      <div className='documents-manager-view'>
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={3} sx={{ gap: 2 }}>
          <Typography variant='h4' component='h1'>
            {t('documents.title')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlySourceDocuments}
                onChange={(event) => this.setState({ showOnlySourceDocuments: event.target.checked })}
              />
            }
            label={t('documents.showOnlySourceDocuments')}
          />
          <Button
            variant='contained'
            startIcon={isUploading ? <CircularProgress size={16} /> : <Upload />}
            onClick={this.handleUploadClick}
            disabled={isUploading}
          >
            {isUploading ? t('documents.uploading') : t('documents.upload')}
          </Button>
        </Box>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box display='flex' justifyContent='center' p={3}>
            <CircularProgress />
          </Box>
        ) : visibleDocuments.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant='body1' color='textSecondary'>
              {t('documents.noDocuments')}
            </Typography>
            <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
              {t('documents.uploadHint')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('documents.fileName')}</TableCell>
                  <TableCell>{t('documents.fileType')}</TableCell>
                  <TableCell>{t('documents.fileSize')}</TableCell>
                  <TableCell>{t('documents.uploadDate')}</TableCell>
                  <TableCell align='center'>{t('documents.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleDocuments.map((document) => (
                  <TableRow key={document.document_id}>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={1}>
                        {this.getFileIcon(document.file_type, document.file_name)}
                        <Typography variant='body2'>{document.file_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={document.file_type || 'unknown'} size='small' variant='outlined' />
                    </TableCell>
                    <TableCell>{this.formatFileSize(document.file_size)}</TableCell>
                    <TableCell>{this.formatDate(document.created_at)}</TableCell>
                    <TableCell align='center'>
                      {isRagSidecar(document.file_name) ? (
                        <Tooltip title={t('documents.sidecarInfo')}>
                          <span style={{ display: 'inline-flex' }}>
                            <Info size={16} color='gray' />
                          </span>
                        </Tooltip>
                      ) : (
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() => this.handleDeleteDocument(document.document_id, document.file_name)}
                          title={t('documents.delete')}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <input
          ref={this.fileInputRef}
          type='file'
          accept={RAG_INDEXABLE_EXTENSIONS.join(',')}
          onChange={this.handleFileSelect}
          style={{ display: 'none' }}
          multiple
        />
      </div>
    );
  }
}

export const DocumentManager = withTranslation()(DocumentManagerBase);
